import { type DocNode } from '@atlaskit/adf-schema';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import { UNSTABLE_configItems } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import type { StreamingCommand } from '../aiStreamingOrchestratorPluginType';

import { isAStreamableCommand } from './utils/is-a-streamable-command';

import { createStreamingCommands } from './index';

const transformer = new JSONTransformer();

type QueuedStreamCommand =
	| {
			actionId: string;
			chunk: string;
			type: 'streamCommand';
	  }
	| { actionId: string; chunk: undefined; type: 'endStream' }
	| { actionId: string; chunk: undefined; command: StreamingCommand; type: 'initStream' };

type API = ExtractInjectionAPI<AIPlugin>;

export class StreamCommandQueue {
	private queue: QueuedStreamCommand[] = [];
	private api: API;
	private adfStreamer: ADFStreamer | undefined;
	private streamingCommands: ReturnType<typeof createStreamingCommands>;

	constructor(api: API) {
		this.api = api;
		this.streamingCommands = createStreamingCommands(api);
	}

	private isActorReady(): boolean {
		try {
			// Check if actor exists and is in streaming state
			const actor = this.api.aiExperience.actions.getAiExperienceActor();
			const currentState = this.api.aiExperience.sharedState.currentState();
			return actor && currentState?.isStreaming === true;
		} catch (error) {
			return false;
		}
	}

	private commandToProcess(commandToProcess: QueuedStreamCommand): Promise<void> {
		const { type, actionId, chunk } = commandToProcess;

		if (type === 'initStream') {
			return this.initStreamPromise(commandToProcess.command);
		} else if (type === 'endStream') {
			return this.endExperiencePromise(actionId);
		} else {
			return this.processStreamCommandPromise(actionId, chunk);
		}
	}

	private processingQueue = false;

	private async processQueue() {
		if (this.processingQueue) {
			return;
		}
		this.processingQueue = true;
		const commandsToProcess = [...this.queue];
		this.queue = [];

		try {
			for (const commandToProcess of commandsToProcess) {
				await this.wrapInTimeout(this.commandToProcess(commandToProcess));
			}
		} finally {
			// Reset the processing queue flag
			this.processingQueue = false;
		}
		// If commands have been added to the queue during async operations, process them now
		if (this.queue.length > 0) {
			this.processQueue();
		}
	}

	// 1 second - this is the maximum time to process a command
	// Since this is purely FE (ie. independent of network reqs)
	// this is more than long enough to process the command
	private COMMAND_TIMEOUT = 1000;

	private async wrapInTimeout(promise: Promise<void>): Promise<void> {
		let timeout: NodeJS.Timeout | undefined;
		try {
			await Promise.race([
				promise,
				new Promise<void>((resolve) => {
					timeout = setTimeout(() => {
						resolve();
					}, this.COMMAND_TIMEOUT);
				}),
			]);
		} finally {
			if (timeout) {
				clearTimeout(timeout);
			}
		}
	}

	/**
	 * End the AI experience
	 */
	public endStreamCommand() {
		// Actor not ready, queue the command
		this.queue.push({ type: 'endStream', actionId: '', chunk: undefined });
		this.processQueue();
	}

	private endExperience() {
		this.api.aiExperience.actions.endAIExperience();
		this.adfStreamer?.reset();
	}

	private async endExperiencePromise(actionId: string): Promise<void> {
		if (this.isStreaming) {
			await this.processStreamCommandPromise(actionId, '', true);
		}
		return new Promise<void>((resolve) => {
			this.endExperience();
			// After we end the experience there are a number of React re-renders that occur immediately after
			// This causes the state to flick between streaming and not streaming, it's not pretty
			// but wrap this in a `requestAnimationFrame` to delay our check until this clears.
			// To fix this properly we probably need to decouple the state with React
			requestAnimationFrame(() => {
				if (!this.api.aiExperience?.sharedState.currentState()?.isStreaming) {
					resolve();
					return;
				}
				const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
					if (!nextSharedState.isStreaming) {
						unsubscribe();
						resolve();
					}
				});
			});
		});
	}

	/**
	 * Streams the AI experience
	 */
	public queueStreamCommand(actionId: string, chunk: string) {
		// Actor not ready, queue the command
		this.queue.push({ type: 'streamCommand', actionId, chunk });
		if (this.isActorReady()) {
			this.processQueue();
		} else {
			const unsubscribe = this.api?.aiExperience.sharedState.onChange(() => {
				if (this.isActorReady()) {
					unsubscribe();
					this.processQueue();
				}
			});
		}
	}

	/**
	 * Ensures the response is a valid document that we are trying to insert
	 * This fixes a number of issues where the BE returns a partial response and context is lost
	 * on insertion
	 */
	private makeDocumentValid(response: DocNode | undefined): DocNode | undefined {
		const schema = this.api.core.sharedState.currentState()?.schema;
		if (!schema || !response) {
			return response;
		}
		try {
			const doc = schema.node('doc', null, [schema.node('paragraph', null, [])]);
			const transform = new Transform(doc);

			const node = PMNode.fromJSON(schema, response);
			const newDoc = transform.replaceWith(0, 2, node).doc;
			return transformer.encode(newDoc) as DocNode;
		} catch (e) {
			return response;
		}
	}

	private processStreamCommand(actionId: string, chunk: string, finalStream = false) {
		const { send } = this.api.aiExperience.actions.getAiExperienceActor();

		// Initialize streamer if needed
		if (!this.adfStreamer) {
			const schema = this.api.core.sharedState.currentState()?.schema;
			this.adfStreamer = schema ? new ADFStreamer(schema) : undefined;
		}

		this.api.core.actions.focus({ scrollIntoView: false });

		if (finalStream) {
			send({
				type: 'complete',
				markdown: '',
				adfResponse: this.makeDocumentValid(this.adfStreamer?.getLastDoc()),
				cacheKey: '',
				modelInput: {},
			});
			return;
		}
		this.adfStreamer?.processChunk(chunk);

		send({
			type: 'stream',
			markdown: '',
			adfResponse: this.makeDocumentValid(this.adfStreamer?.getLastDocWithoutLastLeafNode()),
		});
	}

	private isStreaming = false;

	private processStreamCommandPromise(
		actionId: string,
		chunk: string,
		finalStream = false,
	): Promise<void> {
		this.isStreaming = !finalStream;
		return new Promise<void>((resolve) => {
			setTimeout(() => {
				const chunkBefore = this.api.aiExperience?.sharedState.currentState()?.chunkNumber;
				this.processStreamCommand(actionId, chunk, finalStream);
				if (chunkBefore !== this.api.aiExperience?.sharedState.currentState()?.chunkNumber) {
					resolve();
					return;
				}
				const unsubscribe = this.api.aiExperience?.sharedState.onChange(
					({ nextSharedState, prevSharedState }) => {
						if (nextSharedState?.chunkNumber !== prevSharedState?.chunkNumber) {
							unsubscribe();
							resolve();
						}
					},
				);
			}, 0);
		});
	}

	/**
	 * Streams the AI experience
	 */
	public initStreamCommand(command: StreamingCommand) {
		// If we're already running end the stream and restart
		if (
			this.api.aiExperience.sharedState.currentState()?.aiDecorationExperiencePluginState.isRunning
		) {
			this.queue.push({ type: 'endStream', actionId: '', chunk: undefined });
		}
		this.queue.push({ type: 'initStream', actionId: '', chunk: undefined, command });
		this.processQueue();
	}

	private initStream(command: StreamingCommand) {
		this.streamingCommands[command.type](command);
		this.api.core.actions.focus();

		if (isAStreamableCommand(command)) {
			const isCreate =
				(command.type === 'appendNode' || command.type === 'appendNodeToEnd') &&
				command.streamingType === 'create';
			this.api.aiExperience.actions.openAIModal({
				invokedFrom: 'streamingApi',
				configItem: UNSTABLE_configItems.freeGenerateAifcConfigItem,
				withStreamingHighlight: !isCreate,
			});
		}
	}

	private async initStreamPromise(command: StreamingCommand): Promise<void> {
		await new Promise<void>((resolve) => {
			this.initStream(command);
			const unsubscribe = this.api.aiExperience?.sharedState.onChange(({ nextSharedState }) => {
				if (nextSharedState.aiDecorationExperiencePluginState.isRunning) {
					unsubscribe();
					resolve();
				}
			});
		});
	}

	public destroy() {
		this.queue = [];
		this.adfStreamer = undefined;
	}
}
